<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useNavCollapse } from "@/design/composables/use-nav-collapse";
import Icon from "@/design/primitives/Icon.vue";
import AppNav from "./AppNav.vue";
import { navMessages } from "./i18n/nav";
import NavFooter from "./NavFooter.vue";
import { useRailReveal } from "./use-rail-reveal";
import WorkspaceChip from "./WorkspaceChip.vue";

/**
 * O rail que devolve espaço sem cobrar rótulo.
 *
 * Recolhido, ele ocupava 56px de LAYOUT e voltava a 240px por clique: o preço de
 * ler uma tabela larga era não saber mais o nome de nada, e o preço de saber era
 * 184px de conteúdo. Agora o `<aside>` fica com 56px de layout e o painel de
 * dentro é absoluto — apontar o mouse abre os 240px POR CIMA do conteúdo, sem
 * empurrar uma coluna sequer.
 *
 * ── ONDE O ALFINETE MORA, E ONDE ELE NÃO MORA ────────────────────────────
 *
 * Aqui, na PRIMEIRA LINHA DA PRÓPRIA COLUNA, à direita. É onde a V2 o põe nos
 * três menus que ela tem — `SidebarMenu`, `SettingsSidebar` e `ContactSidebar`
 * desenham o mesmo botão no mesmo canto. Ele passou por esta casca uma temporada
 * na barra de cima, sob um comentário que dizia ser "como na V2"; não era, e a
 * consequência é que o controle da coluna ficava fora dela, entre o hambúrguer e
 * a marca, onde nada mais fala da coluna.
 *
 * A linha de 52px não é a faixa morta que se tirou em 20/08: ela CARREGA — o
 * nome do workspace à esquerda, o alfinete à direita, que é a divisão da V2
 * (onde estou / o que faço com esta coluna). Uma faixa que só guardava um botão
 * era espaço; uma linha que responde "em qual casa" é conteúdo.
 *
 * ── SAIU O MOUSE, FECHA — SEMPRE ─────────────────────────────────────────
 *
 * O foco de TECLADO também abre o painel, porque um rail que só existe sob o
 * ponteiro não existe no Tab. O de MOUSE não: ele ficava preso no link recém
 * clicado e o painel não fechava mais até alguém clicar fora. Quem separa os dois
 * é `use-rail-reveal`, e é lá que a regra está escrita e provada.
 */
const { collapsed, toggle } = useNavCollapse();

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: navMessages });

const { pointer, focus, enter, leave, focusIn, focusOut } = useRailReveal();

/**
 * A lista de workspaces é desenhada FORA do rail (portal): o mouse nela é um
 * `mouseleave` daqui. Sem segurar o painel enquanto ela está aberta, ele fechava,
 * a lista pulava para o lado do rail estreito, o mouse caía de novo no rail e o
 * ciclo piscava sem parar.
 */
const switcherOpen = ref(false);

const revealed = computed(
	() => collapsed.value && (pointer.value || focus.value || switcherOpen.value),
);

/** Aberto por hover ou fixado, o painel tem 240px — só então o rótulo cabe. */
const narrow = computed(() => collapsed.value && !revealed.value);
</script>

<template>
	<!-- O `<aside>` é só o LUGAR que ele ocupa; o painel de dentro é que é
	     desenhado, e recolhido ele passa por cima do conteúdo em vez de o empurrar.
	     A largura anima nos dois para a abertura parecer deliberada e não um
	     repinte. 56 / 240px são as medidas da V2.

	     `bg-nav-bg`/`border-nav-line` e não a superfície genérica: a coluna tem cor
	     PRÓPRIA, lida do CSS que a V2 serve (`bg-white dark:bg-dark`, borda
	     `neutral-300` / `neutral-700` a 50%). No claro dá no mesmo branco; no
	     escuro a V2 pinta `#1a1a1a` e a `surface` da V3 pintava `#212121` — a
	     lateral saía mais clara que a página, o contrário da ordem da V2. Os
	     tokens moram em `tokens.css`, onde está escrito por que são próprios. -->
	<!-- w-54 e não w-60, e w-12.5 e não w-14: o dono pediu o menu ~10% mais
	     curto (26/08) — o corte vale para os DOIS estados, senão recolher devolvia
	     a largura que o pedido tirou. As larguras andam sempre juntas. -->
	<aside class="relative shrink-0 transition-[width] duration-150" :class="collapsed ? 'w-12.5' : 'w-54'">
		<div
			class="absolute inset-y-0 left-0 z-30 flex flex-col overflow-hidden bg-nav-bg transition-[width] duration-150"
			:class="[narrow ? 'w-12.5' : 'w-54', revealed ? 'shadow-rail' : '']"
			@mouseenter="enter"
			@mouseleave="leave"
			@focusin="focusIn"
			@focusout="focusOut"
		>
			<!-- BLOCO 1 — onde estou, e o que faço com esta coluna. Estreito, o
			     px-3 não cabe nos 50px (12+28+12 = 52) e o logo encostava na borda:
			     o padding cede e o chip centra sozinho. -->
			<div
				class="flex h-[52px] shrink-0 items-center gap-1"
				:class="narrow ? 'justify-center px-1' : 'px-3'"
			>
				<WorkspaceChip :collapsed="narrow" class="min-w-0 flex-1" @open="switcherOpen = $event" />
				<!-- Estreito o botão não se desenha: aos 56px ele empurraria o chip para
				     fora, e recolhido o gesto que abre já é o ponteiro. É o `v-if` da V2. -->
				<button
					v-if="!narrow"
					type="button"
					class="flex size-8 shrink-0 items-center justify-center rounded-full text-nav-group transition-colors hover:bg-nav-hover hover:text-nav-fg-strong"
					:title="collapsed ? t('rail.pinOpen') : t('rail.unpin')"
					:aria-label="collapsed ? t('rail.pinLabel') : t('rail.unpinLabel')"
					:aria-pressed="!collapsed"
					@click="toggle"
				>
					<!-- UM ícone, não dois. Solto, o alfinete está deitado 45°; fixado, ele
					     fica de pé e ganha a cor de ênfase — a mesma peça mudando de estado,
					     que é o que o gesto faz. Trocar o desenho inteiro (`pin`/`pinOff`)
					     fazia parecer dois botões alternando no mesmo lugar. -->
					<Icon
						name="pin"
						class="size-3.5 transition-all duration-200"
						:class="collapsed ? 'rotate-45' : 'text-accent'"
					/>
				</button>
			</div>

			<!-- BLOCO 2 — a lista, e só ela rola. -->
			<AppNav :revealed="revealed" />

			<!-- BLOCO 3 — o pé, fora do scroll. -->
			<NavFooter :narrow="narrow" />
		</div>
	</aside>
</template>
